import { useMemo } from 'react'
import { useField, UseFieldConfig, useForm } from 'react-final-form'
import { ARRAY_ERROR, FieldState, FieldSubscription, fieldSubscriptionItems, FieldValidator } from 'final-form'
import isEqualLodash from 'lodash/isEqual'

export interface UseFieldArrayConfig<FieldValue> extends UseFieldConfig<FieldValue[]> {
  isEqual?: (a: any[], b: any[]) => boolean
}

const all: FieldSubscription = fieldSubscriptionItems.reduce(
  (result, key) => {
    result[key] = true

    return result
  },
  {} as Record<string, boolean>
)

type PreparedMutators<FieldValue> = {
  concat: (value: Array<FieldValue>) => void
  insert: (index: number, value: any) => void
  move: (from: number, to: number) => void
  pop: () => any
  push: (value: FieldValue) => void
  remove: (index: number) => any
  removeBatch: (indexes: Array<number>) => any
  shift: () => any
  swap: (indexA: number, indexB: number) => void
  unshift: (value: FieldValue) => void
  update: (index: number, value: any) => void
}

export interface ReturnProps<FieldValue> {
  fields: {
    forEach: (iterator: (name: string, index: number) => void) => void
    map: <Item>(iterator: (name: string, index: number) => Item) => Item[]
    name: string
    value: FieldValue[]
  } & PreparedMutators<FieldValue> &
    FieldState<FieldValue[]>
  meta: Partial<{
    // TODO: Make a diff of `FieldState` without all the functions
    active: boolean
    dirty: boolean
    dirtySinceLastSubmit: boolean
    error: any
    initial: any
    invalid: boolean
    pristine: boolean
    submitError: any
    submitFailed: boolean
    submitSucceeded: boolean
    touched: boolean
    valid: boolean
    visited: boolean
  }>
}

export function useFieldArray<FieldValue>(
  name: string,
  config?: UseFieldArrayConfig<FieldValue>
): ReturnProps<FieldValue> {
  const {
    subscription = all,
    defaultValue,
    initialValue,
    isEqual = isEqualLodash,
    validate: validateProp
  } = config || {}

  const form = useForm('useFieldArray')
  const formMutators = form.mutators
  const hasMutators = !!(formMutators && formMutators.push && formMutators.pop)
  if (!hasMutators) {
    throw new Error('Array mutators not found. You need to provide the mutators from final-form-arrays to your form')
  }

  const mutators = useMemo(
    () =>
      Object.keys(formMutators).reduce((result, key) => {
        // TODO прочекать как работает
        // @ts-ignore
        result[key] = (...args: any[]) => formMutators[key](name, ...args)

        return result
      }, {} as PreparedMutators<FieldValue>),
    [formMutators, name]
  )

  const validate: FieldValidator<any> = useMemo(
    () => (value, allValues, meta) => {
      if (!validateProp) {
        return
      }
      const error = validateProp(value, allValues, meta)
      if (!error || Array.isArray(error)) {
        return error
      }

      return Object.defineProperty([], ARRAY_ERROR, { value: error })
    },
    [validateProp]
  )

  const {
    meta: { length, ...meta },
    input,
    ...fieldState
  } = useField<FieldValue[]>(name, {
    subscription: { ...subscription, length: true },
    defaultValue,
    initialValue,
    isEqual,
    validate,
    format: (v) => v
  })

  const forEach = (iterator: (name: string, index: number) => void): void => {
    const len = length || 0
    for (let i = 0; i < len; i++) {
      iterator(`${name}[${i}]`, i)
    }
  }

  const map = <Item>(iterator: (name: string, index: number) => Item): Item[] => {
    const len = length || 0
    const results: Item[] = []
    for (let i = 0; i < len; i++) {
      results.push(iterator(`${name}[${i}]`, i))
    }

    return results
  }

  return {
    fields: {
      name,
      forEach,
      length: length || 0,
      map,
      ...mutators,
      ...(fieldState as Omit<FieldState<FieldValue[]>, 'name' | 'meta'>),
      value: input.value
    },
    meta
  }
}
